import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Clock, FileDown } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { api, money, API } from "@/lib/api";

const downloadInvoice = (sessionId) => {
  const a = document.createElement("a");
  a.href = `${API}/invoices/${sessionId}`;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useSite();
  const [state, setState] = useState({ phase: "checking", data: null });
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState({ phase: "error", data: null }); return; }
    let attempts = 0; let timer; let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data });
          if (!cleared.current) { clearCart(); cleared.current = true; }
          // Auto-download the PDF invoice once per payment
          const key = `rdx_inv_${sessionId}`;
          if (!localStorage.getItem(key)) { localStorage.setItem(key, "1"); setTimeout(() => downloadInvoice(sessionId), 800); }
          return;
        }
        if (data.payment_status === "expired") { setState({ phase: "expired", data }); return; }
        setState({ phase: "checking", data });
      } catch { /* retry */ }
      attempts += 1;
      if (attempts < 15 && !stop) timer = setTimeout(poll, 2000);
      else setState((s) => ({ ...s, phase: "timeout" }));
    };
    poll();
    return () => { stop = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const d = state.data;
  return (
    <div className="bg-gray-50 py-16 px-4" data-testid="payment-success-page">
      <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-md p-8 text-center">
        {state.phase === "checking" && (<><Loader2 className="w-14 h-14 mx-auto animate-spin brand-text" /><h1 className="text-2xl font-bold mt-4">Verificando tu pago...</h1><p className="text-gray-500 mt-2">Esto toma solo unos segundos.</p></>)}
        {state.phase === "paid" && d && (
          <div className="fade-up">
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500" />
            <h1 className="text-2xl sm:text-3xl font-bold mt-4" data-testid="payment-success-title">¡Pago confirmado!</h1>
            <p className="text-gray-600 mt-2">Gracias {d.customer_name}. Te enviamos la confirmación a tu correo.</p>
            <div className="mt-6 inline-block rounded-lg bg-[var(--brand)]/10 px-5 py-2 brand-text font-bold tracking-wider" data-testid="booking-code">{d.code}</div>
            <div className="mt-6 text-left text-sm divide-y divide-gray-100 border rounded-lg">
              {d.items.map((i, k) => (<div key={k} className="p-3 flex justify-between gap-3"><span>{i.title}<span className="block text-xs text-gray-500">{i.date} · {i.adults} adultos{i.children ? `, ${i.children} niños` : ""}</span></span><span className="font-medium">{money(i.subtotal)}</span></div>))}
              {d.coupon_code && <div className="p-3 flex justify-between text-emerald-600"><span>Cupón {d.coupon_code}</span><span>-{money(d.discount)}</span></div>}
              <div className="p-3 flex justify-between"><span>Total</span><span>{money(d.total)}</span></div>
              <div className="p-3 flex justify-between font-semibold text-emerald-600"><span>Pagado ({d.payment_percent}%)</span><span>{money(d.amount_paid)}</span></div>
              {d.balance_due > 0 && <div className="p-3 flex justify-between text-gray-600"><span>Saldo pendiente</span><span>{money(d.balance_due)}</span></div>}
            </div>
            <button onClick={() => downloadInvoice(sessionId)} data-testid="download-invoice-btn"
              className="btn-accent mt-6 w-full h-12 rounded-md font-semibold flex items-center justify-center gap-2">
              <FileDown className="w-5 h-5" />Descargar factura {d.invoice_number ? `${d.invoice_number} ` : ""}(PDF)
            </button>
            <p className="text-xs text-gray-500 mt-2">Tu factura se descargó automáticamente. Preséntala el día de tu excursión.</p>
          </div>
        )}
        {(state.phase === "timeout" || state.phase === "expired" || state.phase === "error") && (
          <><Clock className="w-14 h-14 mx-auto text-amber-500" /><h1 className="text-2xl font-bold mt-4">{state.phase === "expired" ? "La sesión de pago expiró" : "No pudimos confirmar el pago aún"}</h1><p className="text-gray-500 mt-2">Si se realizó el cargo, recibirás la confirmación por correo. Contáctanos si tienes dudas.</p></>
        )}
        <Link to="/" className="btn-brand inline-block mt-8 px-6 py-3 rounded-md font-semibold" data-testid="back-home-btn">Volver al inicio</Link>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="bg-gray-50 py-20 px-4" data-testid="payment-cancel-page">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-md p-8 text-center">
        <XCircle className="w-16 h-16 mx-auto text-red-400" />
        <h1 className="text-2xl font-bold mt-4">Pago cancelado</h1>
        <p className="text-gray-500 mt-2">No se realizó ningún cargo. Tus excursiones siguen en el carrito.</p>
        <div className="flex justify-center gap-3 mt-8">
          <Link to="/carrito" className="btn-accent px-6 py-3 rounded-md font-semibold">Volver al carrito</Link>
          <Link to="/" className="px-6 py-3 rounded-md font-semibold border border-gray-300 hover:bg-gray-50">Inicio</Link>
        </div>
      </div>
    </div>
  );
}
